import { boiteCentreeV2, boiteTexteV2, centreDecaleV2, collisionsV2, dimensionsTexteV2, texteCentreV2, vueBornee, RAYON_MARQUE_V2, VUE_PAR_DEFAUT_V2, } from './figure-v2.js';
import { fois, milieu, moins, normale, norme, plus, rad, unitaire } from './geometrie.js';
import { polyedreDe } from './solides.js';
/* ============================================================ constantes */
/** Nombre de facettes d'une surface courbe. 48 : à l'écran, indistinguable d'une courbe. */
export const FACETTES_LISSES = 48;
/** Nombre de tranches d'une boule, du pôle sud au pôle nord. */
const PARALLELES_BOULE = 24;
/** Rayon, en unités d'affichage, de la sphère englobante projetée — la figure fait ~240 de large, comme le plan. */
export const RAYON_ENGLOBANT_AFFICHE = 120;
/** Seuil de visibilité d'une face : une face vue par la tranche n'est PAS visible. */
const SEUIL_VISIBLE = 1e-9;
/** Tolérance de recouvrement entre un pointillé et un trait plein, en unités d'affichage. */
const TOLERANCE_RECOUVREMENT = 0.05;
const ECART_TEXTE = 6;
const MARGE_CADRE = 6;
/* ============================================================== vecteurs 3D */
const plus3 = (a, b) => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const moins3 = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const fois3 = (a, k) => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const scalaire3 = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const norme3 = (a) => Math.hypot(a.x, a.y, a.z);
const unitaire3 = (a) => {
    const n = norme3(a);
    return n < 1e-12 ? null : fois3(a, 1 / n);
};
export function repereVue(vue) {
    const v = vueBornee(vue);
    const ct = Math.cos(rad(v.azimut));
    const st = Math.sin(rad(v.azimut));
    const cp = Math.cos(rad(v.elevation));
    const sp = Math.sin(rad(v.elevation));
    return {
        droite: { x: ct, y: 0, z: -st },
        haut: { x: -st * sp, y: cp, z: -ct * sp },
        regard: { x: st * cp, y: sp, z: ct * cp },
    };
}
/**
 * Projection ORTHOGRAPHIQUE d'un point du solide sur l'écran, après rotation
 * par la vue (azimut autour de l'axe vertical `y`, puis élévation). Rend des
 * coordonnées d'ÉCRAN mathématiques : x vers la droite, y vers le HAUT, dans
 * l'unité du solide.
 */
export function projeterVue(p, vue) {
    const r = repereVue(vue);
    return { x: scalaire3(p, r.droite), y: scalaire3(p, r.haut) };
}
/** La profondeur d'un point : plus elle est grande, plus il est proche de l'observateur. */
export function profondeurVue(p, vue) {
    return scalaire3(p, repereVue(vue).regard);
}
/** Les conventions de dimensions de `DimensionsSolideDto`, pour réutiliser `polyedreDe`. */
function dimensionsDto(s) {
    switch (s.famille) {
        case 'cube':
            return { forme: 'cube', arete: s.arete, unite: 'u' };
        case 'prisme_droit':
        case 'pyramide_droite':
            return {
                forme: s.famille === 'prisme_droit' ? 'prisme_droit' : 'pyramide',
                base: s.base,
                coteBase: s.coteBase,
                profondeurBase: s.profondeurBase ?? undefined,
                hauteur: s.hauteur,
                unite: 'u',
            };
        default:
            return null;
    }
}
/** Normale de Newell : robuste pour tout polygone plan, quel que soit son nombre de sommets. */
function normaleNewell(pts) {
    let x = 0;
    let y = 0;
    let z = 0;
    for (let i = 0; i < pts.length; i += 1) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        x += (a.y - b.y) * (a.z + b.z);
        y += (a.z - b.z) * (a.x + b.x);
        z += (a.x - b.x) * (a.y + b.y);
    }
    return { x, y, z };
}
/** Un cercle horizontal discrétisé — le MÊME pour le maillage et pour les cotes. */
function anneau(r, y) {
    return Array.from({ length: FACETTES_LISSES }, (_, i) => {
        const t = (2 * Math.PI * i) / FACETTES_LISSES;
        return { x: r * Math.cos(t), y, z: r * Math.sin(t) };
    });
}
/**
 * Oriente chaque face vers l'extérieur et calcule la sphère englobante.
 *
 * L'orientation est VÉRIFIÉE contre le centre du solide (convexe), pas
 * supposée d'une convention d'écriture : c'est l'erreur de signe que
 * `solides.ts` a connue — des arêtes cachées exactement là où l'on voit.
 */
function finir(b) {
    const n = b.sommets.length;
    const centroide = fois3(b.sommets.reduce((s, p) => plus3(s, p), { x: 0, y: 0, z: 0 }), 1 / n);
    const faces = b.faces.map((f) => {
        const pts = f.sommets.map((i) => b.sommets[i]);
        const nn = unitaire3(normaleNewell(pts)) ?? { x: 0, y: 1, z: 0 };
        const cf = fois3(pts.reduce((s, p) => plus3(s, p), { x: 0, y: 0, z: 0 }), 1 / pts.length);
        const dehors = scalaire3(nn, moins3(cf, centroide)) >= 0;
        return {
            sommets: dehors ? f.sommets : [...f.sommets].reverse(),
            lisse: f.lisse,
            normale: dehors ? nn : fois3(nn, -1),
        };
    });
    const mins = { x: Infinity, y: Infinity, z: Infinity };
    const maxs = { x: -Infinity, y: -Infinity, z: -Infinity };
    for (const p of b.sommets) {
        mins.x = Math.min(mins.x, p.x);
        mins.y = Math.min(mins.y, p.y);
        mins.z = Math.min(mins.z, p.z);
        maxs.x = Math.max(maxs.x, p.x);
        maxs.y = Math.max(maxs.y, p.y);
        maxs.z = Math.max(maxs.z, p.z);
    }
    const centre = fois3(plus3(mins, maxs), 0.5);
    const rayonEnglobant = Math.max(...b.sommets.map((p) => norme3(moins3(p, centre))));
    return { ...b, faces, centre, rayonEnglobant };
}
/**
 * Le maillage d'un solide. Cube, prisme et pyramide viennent de `polyedreDe`
 * (mêmes conventions de dimensions que le v1) ; les corps ronds sont
 * construits ici, facettes lisses. Rend `null` si le solide n'est pas
 * constructible — ce qui n'arrive pas à une figure sortie de
 * `normaliserFigureV2`.
 */
export function maillageDe(solide) {
    const dto = dimensionsDto(solide);
    if (dto !== null) {
        const p = polyedreDe(dto);
        if (p === null)
            return null;
        const noms = [...p.sommets.keys()];
        const indice = new Map(noms.map((nom, i) => [nom, i]));
        const rang = (nom) => Number(nom.slice(1));
        const base = noms.filter((x) => /^A\d+$/.test(x)).sort((a, b) => rang(a) - rang(b));
        const haut = noms.filter((x) => /^S\d+$/.test(x)).sort((a, b) => rang(a) - rang(b));
        const faces = p.faces.map((f) => ({
            sommets: f.map((nom) => indice.get(nom)),
            lisse: false,
        }));
        const toutes = (liste) => (f) => f.length === liste.length && f.every((x) => liste.includes(x));
        const iBase = p.faces.findIndex(toutes(base));
        const iHaut = haut.length > 0 ? p.faces.findIndex(toutes(haut)) : -1;
        return finir({
            sommets: noms.map((nom) => p.sommets.get(nom)),
            faces,
            base: base.map((x) => indice.get(x)),
            haut: haut.map((x) => indice.get(x)),
            apex: indice.get('S') ?? null,
            faceBase: iBase >= 0 ? iBase : null,
            faceHaut: iHaut >= 0 ? iHaut : null,
        });
    }
    const N = FACETTES_LISSES;
    const suivant = (i) => (i + 1) % N;
    switch (solide.famille) {
        case 'cylindre': {
            if (!(solide.rayon > 0) || !(solide.hauteur > 0))
                return null;
            const sommets = [...anneau(solide.rayon, 0), ...anneau(solide.rayon, solide.hauteur)];
            const base = Array.from({ length: N }, (_, i) => i);
            const haut = base.map((i) => i + N);
            const faces = [
                { sommets: base, lisse: false },
                { sommets: haut, lisse: false },
                ...base.map((i) => ({ sommets: [i, suivant(i), suivant(i) + N, i + N], lisse: true })),
            ];
            return finir({ sommets, faces, base, haut, apex: null, faceBase: 0, faceHaut: 1 });
        }
        case 'cone': {
            if (!(solide.rayon > 0) || !(solide.hauteur > 0))
                return null;
            const sommets = [...anneau(solide.rayon, 0), { x: 0, y: solide.hauteur, z: 0 }];
            const base = Array.from({ length: N }, (_, i) => i);
            const faces = [
                { sommets: base, lisse: false },
                ...base.map((i) => ({ sommets: [i, suivant(i), N], lisse: true })),
            ];
            return finir({ sommets, faces, base, haut: [], apex: N, faceBase: 0, faceHaut: null });
        }
        case 'boule': {
            const r = solide.rayon;
            if (!(r > 0))
                return null;
            const P = PARALLELES_BOULE;
            const sommets = [{ x: 0, y: 0, z: 0 }];
            for (let j = 1; j < P; j += 1) {
                const phi = -Math.PI / 2 + (Math.PI * j) / P;
                sommets.push(...anneau(r * Math.cos(phi), r + r * Math.sin(phi)));
            }
            const nord = sommets.length;
            sommets.push({ x: 0, y: 2 * r, z: 0 });
            const cerne = (j, i) => 1 + (j - 1) * N + (i % N);
            const faces = [];
            for (let i = 0; i < N; i += 1) {
                faces.push({ sommets: [0, cerne(1, i + 1), cerne(1, i)], lisse: true });
                for (let j = 1; j < P - 1; j += 1) {
                    faces.push({
                        sommets: [cerne(j, i), cerne(j, i + 1), cerne(j + 1, i + 1), cerne(j + 1, i)],
                        lisse: true,
                    });
                }
                faces.push({ sommets: [cerne(P - 1, i), cerne(P - 1, i + 1), nord], lisse: true });
            }
            return finir({
                sommets,
                faces,
                base: [],
                haut: [],
                apex: null,
                faceBase: null,
                faceHaut: null,
            });
        }
        default:
            return null;
    }
}
const cacheAretes = new WeakMap();
const cacheMaillages = new WeakMap();
function aretesDe(m) {
    const deja = cacheAretes.get(m);
    if (deja)
        return deja;
    const parCle = new Map();
    m.faces.forEach((f, k) => {
        f.sommets.forEach((a, n) => {
            const b = f.sommets[(n + 1) % f.sommets.length];
            const cle = a < b ? `${a}|${b}` : `${b}|${a}`;
            const e = parCle.get(cle);
            if (e)
                e.faces.push(k);
            else
                parCle.set(cle, { i: a, j: b, faces: [k] });
        });
    });
    const aretes = [...parCle.values()].map((e) => {
        const lisses = e.faces.filter((k) => m.faces[k]?.lisse === true).length;
        const nature = lisses === 0 ? 'vive' : lisses === e.faces.length ? 'lisse' : 'bord';
        return { ...e, nature };
    });
    cacheAretes.set(m, aretes);
    return aretes;
}
const vec2Croix = (u, v) => u.x * v.y - u.y * v.x;
const vec2Scal = (u, v) => u.x * v.x + u.y * v.y;
/**
 * Chaque pointillé, amputé de ce que couvrent les traits pleins colinéaires
 * (voir l'en-tête). Ce qui reste de plus court que la tolérance disparaît.
 */
function amputer(caches, pleins) {
    const eps = TOLERANCE_RECOUVREMENT;
    const sortie = [];
    for (const h of caches) {
        const d = moins(h.b, h.a);
        const L = norme(d);
        if (L < eps)
            continue;
        const u = fois(d, 1 / L);
        const couverts = [];
        for (const v of pleins) {
            const va = moins(v.a, h.a);
            const vb = moins(v.b, h.a);
            if (Math.abs(vec2Croix(u, va)) > eps || Math.abs(vec2Croix(u, vb)) > eps)
                continue;
            const ta = vec2Scal(u, va) / L;
            const tb = vec2Scal(u, vb) / L;
            const lo = Math.max(0, Math.min(ta, tb));
            const hi = Math.min(1, Math.max(ta, tb));
            if (hi > lo)
                couverts.push([lo, hi]);
        }
        couverts.sort((x, y) => x[0] - y[0]);
        let t = 0;
        const garder = (t0, t1) => {
            if ((t1 - t0) * L > 2 * eps) {
                sortie.push({ ...h, a: plus(h.a, fois(d, t0)), b: plus(h.a, fois(d, t1)) });
            }
        };
        for (const [lo, hi] of couverts) {
            if (lo > t)
                garder(t, lo);
            t = Math.max(t, hi);
        }
        if (t < 1)
            garder(t, 1);
    }
    return sortie;
}
/** Chaîne bout à bout les morceaux d'une même courbe et d'un même style. */
function chainer(morceaux) {
    const proche = (p, q) => norme(moins(p, q)) < 1e-6;
    const restants = [...morceaux];
    const chaines = [];
    while (restants.length > 0) {
        const m = restants.shift();
        const ch = [m.a, m.b];
        let prolonge = true;
        while (prolonge) {
            prolonge = false;
            for (let i = 0; i < restants.length; i += 1) {
                const q = restants[i];
                const debut = ch[0];
                const fin = ch[ch.length - 1];
                if (proche(q.a, fin))
                    ch.push(q.b);
                else if (proche(q.b, fin))
                    ch.push(q.a);
                else if (proche(q.b, debut))
                    ch.unshift(q.a);
                else if (proche(q.a, debut))
                    ch.unshift(q.b);
                else
                    continue;
                restants.splice(i, 1);
                prolonge = true;
                break;
            }
        }
        chaines.push(ch);
    }
    return chaines;
}
/**
 * Le dessin CALCULÉ d'un solide, pour une vue — celle passée (le geste de
 * l'élève), sinon celle de la figure. Même liste d'affichage que le plan.
 * Le `viewBox` ne dépend PAS de la vue.
 */
export function dessinerEspace(f, vue) {
    const v = vueBornee(vue ?? f.vue ?? VUE_PAR_DEFAUT_V2);
    let m = cacheMaillages.get(f.solide);
    if (m === undefined) {
        m = maillageDe(f.solide);
        cacheMaillages.set(f.solide, m);
    }
    /* Le cadre d'abord : il ne dépend que du solide et des textes de cote. */
    const plusGrandeDemiDiagonale = Math.max(0, ...f.cotes.map((c) => {
        const { largeur, hauteur } = dimensionsTexteV2(c.texte);
        return Math.hypot(largeur / 2, hauteur / 2);
    }));
    const demiCote = RAYON_ENGLOBANT_AFFICHE +
        ECART_TEXTE +
        2 * plusGrandeDemiDiagonale +
        RAYON_MARQUE_V2 +
        MARGE_CADRE;
    const viewBox = { x: -demiCote, y: -demiCote, largeur: 2 * demiCote, hauteur: 2 * demiCote };
    const liste = (primitives) => ({
        espace: 'espace',
        taille: f.taille,
        viewBox,
        rapport: 1,
        primitives,
    });
    if (m === null)
        return liste([]);
    const mm = m;
    const R = repereVue(v);
    const k = RAYON_ENGLOBANT_AFFICHE / mm.rayonEnglobant;
    const ecran = (p) => {
        const q = moins3(p, mm.centre);
        return { x: k * scalaire3(q, R.droite), y: -k * scalaire3(q, R.haut) };
    };
    const profondeur = (p) => scalaire3(moins3(p, mm.centre), R.regard);
    const visible = mm.faces.map((fc) => scalaire3(fc.normale, R.regard) > SEUIL_VISIBLE);
    const S = (i) => mm.sommets[i];
    /* ---- les arêtes */
    const morceaux = [];
    const primitives = [];
    if (f.solide.famille === 'boule') {
        /* La boule : son contour est un cercle EXACT (la projection orthographique
           d'une sphère en est un), et son équateur dit qu'elle est ronde. */
        primitives.push({
            type: 'cercle',
            cx: 0,
            cy: 0,
            r: RAYON_ENGLOBANT_AFFICHE,
            style: 'plein',
            calque: 'figure',
        });
        const n = 2 * FACETTES_LISSES;
        const rb = f.solide.rayon;
        const eq = (i) => {
            const t = (2 * Math.PI * i) / n;
            return plus3(mm.centre, { x: rb * Math.cos(t), y: 0, z: rb * Math.sin(t) });
        };
        for (let i = 0; i < n; i += 1) {
            const a = eq(i);
            const b = eq(i + 1);
            const devant = profondeur(fois3(plus3(a, b), 0.5)) >= 0;
            morceaux.push({
                a: ecran(a),
                b: ecran(b),
                style: devant ? 'plein' : 'pointille',
                courbe: true,
            });
        }
    }
    else {
        for (const e of aretesDe(mm)) {
            const vus = e.faces.map((fk) => visible[fk] === true);
            const une = vus.some(Boolean);
            if (e.nature === 'lisse') {
                if (!(une && !vus.every(Boolean)))
                    continue;
                morceaux.push({ a: ecran(S(e.i)), b: ecran(S(e.j)), style: 'plein', courbe: false });
            }
            else {
                morceaux.push({
                    a: ecran(S(e.i)),
                    b: ecran(S(e.j)),
                    style: une ? 'plein' : 'pointille',
                    courbe: e.nature === 'bord',
                });
            }
        }
    }
    const pleins = morceaux.filter((x) => x.style === 'plein' && norme(moins(x.b, x.a)) >= TOLERANCE_RECOUVREMENT);
    const caches = amputer(morceaux.filter((x) => x.style === 'pointille'), pleins);
    for (const groupe of [caches, pleins]) {
        for (const x of groupe.filter((y) => !y.courbe)) {
            primitives.push({
                type: 'trait',
                x1: x.a.x,
                y1: x.a.y,
                x2: x.b.x,
                y2: x.b.y,
                style: x.style,
                calque: 'figure',
            });
        }
        for (const ch of chainer(groupe.filter((y) => y.courbe))) {
            primitives.push({
                type: 'polyligne',
                points: ch,
                style: groupe[0]?.style ?? 'plein',
                calque: 'figure',
            });
        }
    }
    /* ---- les cotes */
    const cotes = [];
    const textes = [];
    /* Ce que les textes de cote doivent esquiver : tous les tracés déjà posés,
       et les textes et points posés avant eux. Constat au premier rendu : vus de
       face, l'apothème et la hauteur d'une pyramide se projettent sur la MÊME
       droite, et leurs deux textes tombaient l'un sur l'autre. */
    const segmentsVus = [];
    for (const p of primitives) {
        if (p.type === 'trait')
            segmentsVus.push([
                { x: p.x1, y: p.y1 },
                { x: p.x2, y: p.y2 },
            ]);
        if (p.type === 'polyligne') {
            for (let i = 1; i < p.points.length; i += 1) {
                segmentsVus.push([p.points[i - 1], p.points[i]]);
            }
        }
        if (p.type === 'cercle') {
            const n = 2 * FACETTES_LISSES;
            const sur = (i) => ({
                x: p.cx + p.r * Math.cos((2 * Math.PI * i) / n),
                y: p.cy + p.r * Math.sin((2 * Math.PI * i) / n),
            });
            for (let i = 0; i < n; i += 1)
                segmentsVus.push([sur(i), sur(i + 1)]);
        }
    }
    const boitesVues = [];
    /**
     * Le texte d'une cote, à côté de son segment, de préférence du côté opposé
     * au centre de la figure et au milieu ; sinon l'autre côté, ou plus loin le
     * long du segment — la pose qui heurte le moins de choses. L'ancre reste SUR
     * le segment, donc dans la sphère englobante : le cadre fixe la contient.
     */
    const legender = (p1, p2, texte) => {
        const mil = milieu(p1, p2);
        const u = unitaire(moins(p2, p1));
        let n = u === null ? (unitaire(mil) ?? { x: 0, y: -1 }) : normale(u);
        const sc = vec2Scal(n, mil);
        if (u !== null) {
            if (Math.abs(sc) < 1e-6 * RAYON_ENGLOBANT_AFFICHE) {
                if (n.y > 1e-9 || (Math.abs(n.y) <= 1e-9 && n.x < 0))
                    n = fois(n, -1);
            }
            else if (sc < 0)
                n = fois(n, -1);
        }
        const { largeur, hauteur } = dimensionsTexteV2(texte);
        let meilleur = centreDecaleV2(mil, n, ECART_TEXTE, largeur, hauteur);
        let cout = Infinity;
        for (const t of u === null ? [0.5] : [0.5, 0.35, 0.65, 0.2, 0.8]) {
            for (const [signe, penalite] of [
                [1, 0],
                [-1, 0.3],
            ]) {
                const ancre = plus(p1, fois(moins(p2, p1), t));
                const c = centreDecaleV2(ancre, fois(n, signe), ECART_TEXTE, largeur, hauteur);
                const k = 10 * collisionsV2(boiteCentreeV2(c, largeur, hauteur), segmentsVus, boitesVues) +
                    penalite +
                    Math.abs(t - 0.5);
                if (k < cout - 1e-9) {
                    cout = k;
                    meilleur = c;
                }
            }
        }
        const txt = texteCentreV2(meilleur, texte, 'cote');
        textes.push(txt);
        boitesVues.push(boiteTexteV2(txt));
    };
    const segmentCote = (a, b, style) => {
        const pa = ecran(a);
        const pb = ecran(b);
        cotes.push({ type: 'trait', x1: pa.x, y1: pa.y, x2: pb.x, y2: pb.y, style, calque: 'cote' });
        segmentsVus.push([pa, pb]);
    };
    const marque = (p) => {
        const q = ecran(p);
        cotes.push({ type: 'marque', cx: q.x, cy: q.y, r: RAYON_MARQUE_V2 });
        boitesVues.push(boiteCentreeV2(q, 2 * RAYON_MARQUE_V2, 2 * RAYON_MARQUE_V2));
    };
    /** Le sommet d'une liste dont la projection est la plus à droite — sur la silhouette. */
    const plusADroite = (ids) => ids.reduce((best, i) => (ecran(S(i)).x > ecran(S(best)).x ? i : best), ids[0]);
    /** Direction horizontale « vers l'avant et la droite » : un rayon y reste lisible pour toute vue. */
    const avantDroite = unitaire3({
        x: R.droite.x + R.regard.x,
        y: 0,
        z: R.droite.z + R.regard.z,
    }) ?? { x: 1, y: 0, z: 0 };
    const faceVue = (i) => i !== null && visible[i] === true;
    const aretesBase = () => mm.base.map((i, n) => [i, mm.base[(n + 1) % mm.base.length]]);
    const plusProche = (paires) => paires.reduce((best, pq) => {
        const pm = fois3(plus3(S(pq[0]), S(pq[1])), 0.5);
        if (best === null)
            return pq;
        const bm = fois3(plus3(S(best[0]), S(best[1])), 0.5);
        return profondeur(pm) > profondeur(bm) ? pq : best;
    }, null);
    const s = f.solide;
    for (const c of f.cotes) {
        switch (c.grandeur) {
            case 'hauteur':
            case 'arete': {
                if ((s.famille === 'cone' || s.famille === 'pyramide_droite') && mm.apex !== null) {
                    /* La hauteur d'un cône ou d'une pyramide est INTÉRIEURE : l'axe, en
                       pointillé, avec son angle droit au pied. C'est ce qui la
                       distingue de l'apothème, l'erreur classique. */
                    const pied = { x: 0, y: 0, z: 0 };
                    const sommet = S(mm.apex);
                    segmentCote(sommet, pied, 'pointille');
                    const cote = 0.12 * Math.min(mm.rayonEnglobant, s.hauteur);
                    const a = plus3(pied, fois3(avantDroite, cote));
                    const b = plus3(a, { x: 0, y: cote, z: 0 });
                    const d = plus3(pied, { x: 0, y: cote, z: 0 });
                    segmentsVus.push([ecran(a), ecran(b)], [ecran(b), ecran(d)]);
                    cotes.push({
                        type: 'carre_droit',
                        points: [ecran(a), ecran(b), ecran(d)],
                        style: 'plein',
                        calque: 'cote',
                    });
                    legender(ecran(sommet), ecran(pied), c.texte);
                }
                else if (mm.haut.length === mm.base.length && mm.base.length > 0) {
                    /* Prisme, cube, cylindre : l'arête latérale (ou la génératrice) la
                       plus à droite, qui est sur la silhouette. Déjà tracée. */
                    const i = plusADroite(mm.base);
                    const j = mm.haut[mm.base.indexOf(i)];
                    legender(ecran(S(i)), ecran(S(j)), c.texte);
                }
                break;
            }
            case 'rayon':
            case 'diametre': {
                let centre;
                let face;
                if (s.famille === 'boule') {
                    centre = mm.centre;
                    face = null;
                }
                else if (s.famille === 'cylindre' && v.elevation >= 0) {
                    centre = { x: 0, y: s.hauteur, z: 0 };
                    face = mm.faceHaut;
                }
                else {
                    centre = { x: 0, y: 0, z: 0 };
                    face = mm.faceBase;
                }
                const r = 'rayon' in s ? s.rayon : 0;
                /* Sur la boule, le rayon va vers le bord droit du contour. */
                const dir = s.famille === 'boule' ? R.droite : avantDroite;
                const bout = plus3(centre, fois3(dir, r));
                const depart = c.grandeur === 'rayon' ? centre : plus3(centre, fois3(dir, -r));
                segmentCote(depart, bout, faceVue(face) ? 'plein' : 'pointille');
                marque(centre);
                legender(ecran(depart), ecran(bout), c.texte);
                break;
            }
            case 'apotheme': {
                if (mm.apex === null)
                    break;
                const sommet = S(mm.apex);
                if (s.famille === 'cone') {
                    const i = plusADroite(mm.base);
                    legender(ecran(sommet), ecran(S(i)), c.texte);
                }
                else {
                    /* Pyramide : vers le milieu du côté de base de la face latérale la
                       plus tournée vers l'observateur. */
                    let meilleure = -Infinity;
                    let pied = null;
                    let vue = false;
                    mm.faces.forEach((fc, fk) => {
                        if (!fc.sommets.includes(mm.apex))
                            return;
                        const d = scalaire3(fc.normale, R.regard);
                        if (d <= meilleure)
                            return;
                        const autres = fc.sommets.filter((x) => x !== mm.apex);
                        if (autres.length !== 2)
                            return;
                        meilleure = d;
                        pied = fois3(plus3(S(autres[0]), S(autres[1])), 0.5);
                        vue = visible[fk] === true;
                    });
                    if (pied === null)
                        break;
                    segmentCote(sommet, pied, vue ? 'plein' : 'pointille');
                    legender(ecran(sommet), ecran(pied), c.texte);
                }
                break;
            }
            case 'cote_base':
            case 'profondeur_base': {
                if (!('coteBase' in s))
                    break;
                const voulue = c.grandeur === 'cote_base' ? s.coteBase : (s.profondeurBase ?? s.coteBase);
                const paires = aretesBase().filter(([i, j]) => Math.abs(norme3(moins3(S(i), S(j))) - voulue) < 1e-6 * Math.max(1, voulue));
                const pq = plusProche(paires);
                if (pq !== null)
                    legender(ecran(S(pq[0])), ecran(S(pq[1])), c.texte);
                break;
            }
            case 'diagonale_base': {
                if (mm.base.length !== 4)
                    break;
                const a = S(mm.base[0]);
                const b = S(mm.base[2]);
                segmentCote(a, b, faceVue(mm.faceBase) ? 'plein' : 'pointille');
                legender(ecran(a), ecran(b), c.texte);
                break;
            }
        }
    }
    return liste([...primitives, ...cotes, ...textes]);
}
//# sourceMappingURL=vue3d.js.map